'use strict';

// Generic ES256K (secp256k1 ECDSA) JWT signing/verification, shared by VC-JWTs (vcjwt.js)
// and "Sign in with Web3Keys" id_tokens. Signatures are raw 64-byte r‖s (JOSE), signed with
// the user's mnemonic-derived identity key via @smartledger/bsv; public keys are secp256k1 JWKs.

const { getBsv, subtle } = require('./lib');
const { toB64Url, fromB64Url } = require('./b64');

const enc = (s) => new TextEncoder().encode(s);
const dec = (b) => new TextDecoder().decode(b);
const jsonToB64Url = (obj) => toB64Url(enc(JSON.stringify(obj)));
const b64UrlToJson = (s) => JSON.parse(dec(fromB64Url(s)));
const nowSec = () => Math.floor(Date.now() / 1000);

function sha256(input) {
  const bsv = getBsv();
  const B = bsv.deps.Buffer;
  return bsv.crypto.Hash.sha256(typeof input === 'string' ? B.from(input, 'utf8') : B.from(input));
}

// secp256k1 bsv PublicKey -> JWK / JWK -> bsv PublicKey.
function jwkFromPublicKey(pub) {
  const x = new Uint8Array(pub.point.getX().toBuffer({ size: 32 }));
  const y = new Uint8Array(pub.point.getY().toBuffer({ size: 32 }));
  return { kty: 'EC', crv: 'secp256k1', x: toB64Url(x), y: toB64Url(y) };
}
function publicKeyFromJwk(jwk) {
  const bsv = getBsv();
  const B = bsv.deps.Buffer;
  const px = bsv.crypto.BN.fromBuffer(B.from(fromB64Url(jwk.x)));
  const py = bsv.crypto.BN.fromBuffer(B.from(fromB64Url(jwk.y)));
  return bsv.PublicKey.fromPoint(new bsv.crypto.Point(px, py));
}

/** Sign a JWT (header+payload) with a bsv secp256k1 PrivateKey -> compact JWS (ES256K). */
function signJwt(header, payload, privateKey) {
  const bsv = getBsv();
  const signingInput = jsonToB64Url(header) + '.' + jsonToB64Url(payload);
  const sig = bsv.crypto.ECDSA.sign(sha256(signingInput), privateKey);
  const r = new Uint8Array(sig.r.toBuffer({ size: 32 }));
  const s = new Uint8Array(sig.s.toBuffer({ size: 32 }));
  const jose = new Uint8Array(64); jose.set(r, 0); jose.set(s, 32);
  return signingInput + '.' + toB64Url(jose);
}

/** Decode a JWT without verifying. */
function decodeJwt(jwt) {
  const [h, p] = String(jwt).split('.');
  return { header: b64UrlToJson(h), payload: b64UrlToJson(p) };
}

// secp256k1 (ES256K, current) signature check via bsv.
function verifySecp256k1(signingInput, sigBytes, jwk) {
  const bsv = getBsv();
  const B = bsv.deps.Buffer;
  const r = bsv.crypto.BN.fromBuffer(B.from(sigBytes.slice(0, 32)));
  const ss = bsv.crypto.BN.fromBuffer(B.from(sigBytes.slice(32, 64)));
  return bsv.crypto.ECDSA.verify(sha256(signingInput), new bsv.crypto.Signature(r, ss), publicKeyFromJwk(jwk));
}

// P-256 (ES256, LEGACY — credentials issued before the ES256K switch) via WebCrypto. The raw
// 64-byte r‖s is exactly the IEEE-P1363 form WebCrypto expects.
async function verifyP256(signingInput, sigBytes, jwk) {
  const key = await subtle().importKey('jwk', { kty: 'EC', crv: 'P-256', x: jwk.x, y: jwk.y }, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
  return subtle().verify({ name: 'ECDSA', hash: 'SHA-256' }, key, sigBytes, new TextEncoder().encode(signingInput));
}

/**
 * Verify a JWT signature (+ exp) against a JWKS. Handles BOTH the current ES256K (secp256k1)
 * and the legacy ES256 (P-256) credentials. Does NOT check iss/aud/nonce.
 */
async function verifyJwt(jwt, jwks) {
  try {
    const parts = String(jwt).split('.');
    if (parts.length !== 3) return { valid: false, reason: 'malformed JWT' };
    const [h, p, s] = parts;
    const header = b64UrlToJson(h);
    const payload = b64UrlToJson(p);
    const keys = (jwks && jwks.keys) || [];
    const jwk = keys.find((k) => k.kid === header.kid) || keys[0];
    if (!jwk) return { valid: false, reason: 'no matching key in JWKS', header, payload };
    const sigBytes = fromB64Url(s);
    if (sigBytes.length !== 64) return { valid: false, reason: 'bad signature length', header, payload };
    const legacy = header.alg === 'ES256' || jwk.crv === 'P-256';
    let ok;
    try { ok = legacy ? await verifyP256(h + '.' + p, sigBytes, jwk) : verifySecp256k1(h + '.' + p, sigBytes, jwk); }
    catch (e) { return { valid: false, reason: (e && e.message) || 'verify error', header, payload }; }
    if (!ok) return { valid: false, reason: 'bad signature', header, payload };
    if (payload.exp && nowSec() >= payload.exp) return { valid: false, reason: 'expired', header, payload };
    return { valid: true, header, payload };
  } catch (e) {
    return { valid: false, reason: (e && e.message) || String(e) };
  }
}

/**
 * "Sign in with Web3Keys" id_token: a JWT signed by the user's identity key asserting their DID.
 * @param {object} o - { privateKey (bsv), issuerDid, audience (client_id), nonce, ttlSec=300, kid='key-1' }
 */
function signIdToken({ privateKey, issuerDid, audience, nonce, ttlSec = 300, kid = 'key-1' }) {
  const iat = nowSec();
  const header = { alg: 'ES256K', typ: 'JWT', kid };
  const payload = { iss: issuerDid, sub: issuerDid, aud: audience, nonce, iat, exp: iat + ttlSec };
  return signJwt(header, payload, privateKey);
}

/** Verify an id_token: signature + exp + that aud/nonce/iss match what's expected. */
async function verifyIdToken(jwt, jwks, { audience, nonce } = {}) {
  const r = await verifyJwt(jwt, jwks);
  if (!r.valid) return r;
  const p = r.payload;
  const bad = (reason) => ({ valid: false, reason, header: r.header, payload: p });
  if (!p.iss) return bad('missing issuer');
  if (audience && p.aud !== audience) return bad('audience mismatch');
  if (nonce && p.nonce !== nonce) return bad('nonce mismatch');
  return { valid: true, header: r.header, payload: p, did: p.iss };
}

module.exports = { signJwt, decodeJwt, verifyJwt, signIdToken, verifyIdToken, jwkFromPublicKey, publicKeyFromJwk, nowSec };
